import { create } from 'zustand';
import { ItemFollowQueryService } from '../services/ItemFollowQueryService';
import { useAuthStore } from './authStore';

/**
 * How long a loaded list of followed items is trusted before the next screen
 * that needs it asks again. Long enough that one screen full of toggles
 * shares a single request; short enough that a follow made in another tab or
 * on another device shows up without a reload.
 */
export const ITEM_FOLLOW_MAX_AGE_MS = 60_000;

export type ItemFollowLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface ItemFollowStoreState {
	/** Whose follows are held, as `<tenant>:<user>`. Null = nothing loaded. */
	ownerKey: string | null;
	status: ItemFollowLoadStatus;
	/** When the list was last loaded (epoch ms); 0 = never. */
	loadedAt: number;
	followedItemIds: ReadonlySet<number>;
	/** Items with a follow or unfollow request in flight. */
	savingItemIds: ReadonlySet<number>;
}

export interface ItemFollowStoreActions {
	/**
	 * Make sure the signed-in user's followed items are loaded and recent.
	 * Any number of toggles can call this as they mount: they share one
	 * request.
	 */
	ensureLoaded: () => Promise<void>;
	/**
	 * Follow or unfollow an item. The change shows at once and is put back if
	 * the request fails. Resolves true when it was saved.
	 */
	setFollowing: (itemId: number, isFollowing: boolean) => Promise<boolean>;
	reset: () => void;
}

const initialState: ItemFollowStoreState = {
	ownerKey: null,
	status: 'idle',
	loadedAt: 0,
	followedItemIds: new Set<number>(),
	savingItemIds: new Set<number>(),
};

/**
 * Who the follows belong to. A different tenant, a different user, or a
 * delegated session (which swaps the user) all change the key, so one
 * person's follows are never shown to another.
 */
export function currentItemFollowOwnerKey(): string | null {
	const { user, instance } = useAuthStore.getState();

	if (user == null) {
		return null;
	}

	return `${instance?.alias ?? ''}:${user.instanceUserId ?? user.id}`;
}

function withItem(
	ids: ReadonlySet<number>,
	itemId: number,
	isPresent: boolean,
): ReadonlySet<number> {
	const next = new Set(ids);

	if (isPresent) {
		next.add(itemId);
	} else {
		next.delete(itemId);
	}

	return next;
}

// The request shared by every caller that asked while it was in flight.
let loadInFlight: Promise<void> | null = null;

// Counts follow and unfollow requests as they start and as they finish. A
// list that was being fetched while one of them ran may or may not include
// it, so a load that sees this number move fetches again instead of trusting
// what it got.
let followChangeCount = 0;

/** How many times one load fetches again because a follow changed under it. */
const MAX_LOAD_ATTEMPTS = 3;

/**
 * The signed-in user's followed items (PD-14306), held once for every follow
 * toggle on screen. A list of fifty out-of-stock rows would otherwise ask the
 * server fifty times, or need a column added to every list query; this loads
 * the user's (small) set of followed item ids once and lets each toggle read
 * from it.
 *
 * Not persisted: follows live on the server, and a stale copy from an earlier
 * session would only show the wrong state until the first load finished.
 */
export const useItemFollowStore = create<
	ItemFollowStoreState & ItemFollowStoreActions
>()((set, get) => ({
	...initialState,

	ensureLoaded: async () => {
		const ownerKey = currentItemFollowOwnerKey();

		if (ownerKey == null) {
			return;
		}

		const state = get();
		const isSameOwner = state.ownerKey === ownerKey;

		// A recent answer is reused, and so is a recent failure. A list has a
		// toggle on every out-of-stock row, and rows mount as the user scrolls:
		// without the second half, a user who may not follow items (the request
		// is refused) would send a new request for every row that scrolls in.
		if (
			isSameOwner &&
			(state.status === 'ready' || state.status === 'error') &&
			Date.now() - state.loadedAt < ITEM_FOLLOW_MAX_AGE_MS
		) {
			return;
		}

		if (isSameOwner && loadInFlight != null) {
			return loadInFlight;
		}

		// Refreshing a list that is already on screen keeps it there while the
		// new one loads, so the toggles do not blink out every time it ages. A
		// different user's follows, though, must not stay on screen while the
		// new user's load.
		const isRefresh = isSameOwner && state.status === 'ready';

		if (!isRefresh) {
			set(
				isSameOwner
					? { status: 'loading' }
					: { ...initialState, ownerKey, status: 'loading' },
			);
		}

		const request = (async (): Promise<void> => {
			try {
				let itemIds: Array<number> = [];

				for (let attempt = 0; attempt < MAX_LOAD_ATTEMPTS; attempt++) {
					const changesBefore = followChangeCount;

					itemIds = await ItemFollowQueryService.getMyFollowedItemIds();

					if (followChangeCount === changesBefore) {
						break;
					}
				}

				// The user changed while the request was out: drop the answer.
				if (get().ownerKey !== ownerKey) {
					return;
				}

				// A follow still being saved is newer than the list, so what the
				// toggle shows for it wins over what the server said a moment ago.
				const loaded = new Set(itemIds);
				const { followedItemIds, savingItemIds } = get();

				for (const itemId of savingItemIds) {
					if (followedItemIds.has(itemId)) {
						loaded.add(itemId);
					} else {
						loaded.delete(itemId);
					}
				}

				set({ status: 'ready', loadedAt: Date.now(), followedItemIds: loaded });
			} catch (error) {
				console.debug('[itemFollowStore] Could not load followed items', error);

				if (get().ownerKey !== ownerKey) {
					return;
				}

				// Either way the attempt is timestamped, so the next screen to open
				// waits before asking again. A failed refresh keeps the list that
				// was already showing; a failed first load has nothing to show.
				set(
					isRefresh
						? { loadedAt: Date.now() }
						: { status: 'error', loadedAt: Date.now() },
				);
			}
		})();

		loadInFlight = request;

		try {
			await request;
		} finally {
			if (loadInFlight === request) {
				loadInFlight = null;
			}
		}
	},

	setFollowing: async (itemId, isFollowing) => {
		const ownerKey = currentItemFollowOwnerKey();

		if (ownerKey == null || get().savingItemIds.has(itemId)) {
			return false;
		}

		// Whatever is held belongs to someone else (the user or tenant changed
		// and nothing has been loaded for the new one yet): start clean rather
		// than adding this item to another person's list.
		if (get().ownerKey !== ownerKey) {
			set({ ...initialState, ownerKey });
		}

		const wasFollowing = get().followedItemIds.has(itemId);

		followChangeCount++;
		set((state) => ({
			followedItemIds: withItem(state.followedItemIds, itemId, isFollowing),
			savingItemIds: withItem(state.savingItemIds, itemId, true),
		}));

		try {
			if (isFollowing) {
				await ItemFollowQueryService.follow(itemId);
			} else {
				await ItemFollowQueryService.unfollow(itemId);
			}

			if (get().ownerKey === ownerKey) {
				set((state) => ({
					savingItemIds: withItem(state.savingItemIds, itemId, false),
				}));
			}

			return true;
		} catch (error) {
			console.debug('[itemFollowStore] Could not save the follow', error);

			if (get().ownerKey === ownerKey) {
				set((state) => ({
					followedItemIds: withItem(
						state.followedItemIds,
						itemId,
						wasFollowing,
					),
					savingItemIds: withItem(state.savingItemIds, itemId, false),
				}));
			}

			return false;
		} finally {
			followChangeCount++;
		}
	},

	reset: () => {
		loadInFlight = null;
		followChangeCount = 0;
		set({ ...initialState });
	},
}));
